import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function slugifyHeading(text, index) {
  return `toc-${index}-${String(text)
    .toLowerCase()
    .replace(/[#*_`~]/g, '')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')}`;
}

export function getH2Headings(markdown = '') {
  return [...markdown.matchAll(/^##\s+(.+)$/gm)].map((match, index) => {
    const text = match[1]
      .replace(/[#*_`~]/g, '')
      .replace(/[^\w\s-]/g, '')
      .trim();

    return {
      id: slugifyHeading(text, index),
      text,
    };
  });
}

export default function TableOfContents({ headings, isDarkMode }) {
  const [activeId, setActiveId] = useState(headings?.[0]?.id || '');

  useEffect(() => {
    if (!headings || headings.length < 3) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top,
          )[0];

        if (visible?.target?.id) {
          setActiveId(visible.target.id);
        }
      },
      {
        rootMargin: '-25% 0px -65% 0px',
        threshold: 0.1,
      },
    );

    headings.forEach((heading) => {
      const el = document.getElementById(heading.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [headings]);

  if (!headings || headings.length < 3) return null;

  return (
    <AnimatePresence>
      <motion.aside
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 20 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={`hidden min-[1280px]:block sticky top-6 self-start w-44 shrink-0 border-l pl-4 ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}
      >
        <p
          className={`mb-3 text-[11px] font-bold uppercase tracking-[0.18em] ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`}
        >
          On this page
        </p>

        <nav className="space-y-2">
          {headings.map((heading) => {
            const active = activeId === heading.id;

            return (
              <button
                key={heading.id}
                type="button"
                onClick={() => {
                  setActiveId(heading.id);
                  document.getElementById(heading.id)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                className={`relative block w-full py-1 pl-3 text-left text-sm transition ${
                  active
                    ? isDarkMode
                      ? 'text-indigo-200 font-semibold'
                      : 'text-gray-900 font-semibold'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {active && (
                  <motion.span
                    layoutId="toc-active-indicator"
                    className={`absolute left-[-17px] top-0 h-full w-[2px] ${
                      isDarkMode ? 'bg-indigo-300' : 'bg-gray-900'
                    }`}
                    transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                  />
                )}
                {heading.text}
              </button>
            );
          })}
        </nav>
      </motion.aside>
    </AnimatePresence>
  );
}
